/**
 * Business-state interface for the Virtual Twin (read surface).
 *
 * This is the same shape the production backend would expose over the shared API
 * (getFeaturedMenu / listMenuItems / getShopInfo …). The splash page only needs
 * the read surface, but it is modelled cleanly so the seam is honest: swap the
 * localStorage-backed implementation here for HTTP calls and nothing upstream
 * changes. Grounding guarantee: callers get only `is_available && is_active`
 * items — the page literally cannot render a drink the shop can't make.
 */
import type {
  Category,
  MenuItemWithDetails,
  ShopInfo,
} from './types';
import { SHOP_INFO } from './seed';
import { loadState } from './store';

function joinItemDetails(): MenuItemWithDetails[] {
  const state = loadState();
  const categoryById = new Map<string, Category>(
    state.categories.map((c) => [c.id, c]),
  );
  const optionsByItem = new Map<string, MenuItemWithDetails['options']>();
  for (const opt of state.menu_item_options) {
    const list = optionsByItem.get(opt.menu_item_id) ?? [];
    list.push(opt);
    optionsByItem.set(opt.menu_item_id, list);
  }

  return state.menu_items.map((item) => ({
    ...item,
    category: categoryById.get(item.category_id) ?? null,
    options: optionsByItem.get(item.id) ?? [],
  }));
}

function categorySort(a: MenuItemWithDetails, b: MenuItemWithDetails): number {
  const ca = a.category?.sort_order ?? Number.MAX_SAFE_INTEGER;
  const cb = b.category?.sort_order ?? Number.MAX_SAFE_INTEGER;
  if (ca !== cb) return ca - cb;
  return a.name.localeCompare(b.name);
}

/** All sellable menu items (available + active), with category + options joined. */
export function listMenuItems(): MenuItemWithDetails[] {
  return joinItemDetails()
    .filter((item) => item.is_available && item.is_active)
    .sort(categorySort);
}

/**
 * The curated Featured strip: explicitly-flagged items that are also available.
 * Falls back to "first N available" if no item is flagged, so the strip is never
 * empty. Capped at `limit` (default 6) per the 3–6 card guidance.
 */
export function getFeaturedMenu(limit = 6): MenuItemWithDetails[] {
  const available = listMenuItems();
  const flagged = available.filter((item) => item.is_featured);
  const chosen = flagged.length > 0 ? flagged : available;
  return chosen.slice(0, limit);
}

/** All active categories, in display order. */
export function listCategories(): Category[] {
  return loadState()
    .categories.filter((c) => c.is_active)
    .sort((a, b) => a.sort_order - b.sort_order);
}

/** Static shop info (hours / location) — single source of truth shared with admin. */
export function getShopInfo(): ShopInfo {
  return SHOP_INFO;
}

export { formatPrice, computeOpenStatus, todayHoursLabel, weekdayName } from './format';
export { resetState } from './store';
export type {
  Category,
  MenuItem,
  MenuItemOption,
  MenuItemWithDetails,
  OpenStatus,
  ShopInfo,
  Weekday,
} from './types';
